import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

export default function CanvasZoomControls({
  canvasRef,
  onZoomIn,
  onZoomOut,
  onReset,
}) {
  const cardRef = useRef(null);

  useEffect(() => {
    let animationFrameId;

    const updatePosition = () => {
      if (!canvasRef?.current || !cardRef.current) return;
      const canvasEl = canvasRef.current;
      const cardEl = cardRef.current;

      const rect = canvasEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const cardHeight =
        cardEl.getBoundingClientRect().height ||
        cardEl.offsetHeight ||
        86;

      // Determine the bottom limit in the viewport so controls are never covered by the sticky footer
      let maxViewportBottom = viewportHeight - 15;
      const footerEl = document.querySelector("footer");
      if (footerEl) {
        const footerRect = footerEl.getBoundingClientRect();
        // If the sticky footer is visible in the viewport, stay 15px above its top edge
        if (footerRect.top < viewportHeight && footerRect.bottom > 0) {
          maxViewportBottom = Math.min(maxViewportBottom, footerRect.top - 15);
        }
      } else {
        const fallbackFooterHeight = window.innerWidth <= 768 ? 100 : 50;
        maxViewportBottom = viewportHeight - fallbackFooterHeight - 15;
      }

      // Stick above the sticky footer while inside canvas, and dock 15px above canvas bottom
      const visibleBottomInViewport = Math.min(maxViewportBottom, rect.bottom - 15);
      const targetY = visibleBottomInViewport - rect.top - cardHeight;
      const maxAllowedY = Math.max(15, rect.height - cardHeight - 15);
      const clampedY = Math.max(15, Math.min(targetY, maxAllowedY));

      // Show controls only when:
      // 1) targetY >= 15 (canvas has scrolled sufficiently into view above sticky footer)
      // 2) canvas has entered viewport (rect.top < viewportHeight)
      // 3) canvas hasn't scrolled completely above viewport (rect.bottom > cardHeight + 30)
      const isVisible =
        targetY >= 15 &&
        rect.top < viewportHeight &&
        rect.bottom > (cardHeight + 30);

      if (!isVisible) {
        cardEl.style.opacity = "0";
        cardEl.style.pointerEvents = "none";
        return;
      }

      cardEl.style.opacity = "1";
      cardEl.style.pointerEvents = "auto";
      cardEl.style.transform = `translate3d(0, ${Math.round(clampedY)}px, 0)`;
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(updatePosition);
    };

    window.addEventListener("scroll", onScrollOrResize, { passive: true, capture: true });
    window.addEventListener("resize", onScrollOrResize);

    let resizeObserver;
    if (window.ResizeObserver && canvasRef.current) {
      resizeObserver = new ResizeObserver(onScrollOrResize);
      resizeObserver.observe(canvasRef.current);
    }

    updatePosition();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("scroll", onScrollOrResize, { capture: true });
      window.removeEventListener("resize", onScrollOrResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [canvasRef]);

  return (
    <div
      ref={cardRef}
      className="canvas-zoom-control-card nodrag nopan"
      style={{
        position: "absolute",
        top: 0,
        left: 15,
        zIndex: 20,
        opacity: 0,
        pointerEvents: "none",
        transition: "opacity 0.2s ease",
      }}
    >
      <button
        type="button"
        onClick={onZoomIn}
        className="canvas-zoom-control-btn"
        title="Zoom in"
        aria-label="Zoom in"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="14" height="14" fill="currentColor">
          <path d="M26 14h-8V6a2 2 0 0 0-4 0v8H6a2 2 0 0 0 0 4h8v8a2 2 0 0 0 4 0v-8h8a2 2 0 0 0 0-4z" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onZoomOut}
        className="canvas-zoom-control-btn"
        title="Zoom out"
        aria-label="Zoom out"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="14" height="14" fill="currentColor">
          <path d="M26 14H6a2 2 0 0 0 0 4h20a2 2 0 0 0 0-4z" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onReset}
        className="canvas-zoom-control-btn"
        title="Reset to default view"
        aria-label="Reset to default view"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 30" width="14" height="14" fill="currentColor">
          <path d="M3.692 4.63c0-.53.4-.938.939-.938h5.215V0H4.708C2.13 0 0 2.054 0 4.63v5.216h3.692V4.631zM27.354 0h-5.2v3.692h5.17c.53 0 .984.4.984.939v5.215H32V4.631A4.624 4.624 0 0027.354 0zm.954 24.83c0 .532-.4.94-.939.94h-5.215v3.768h5.215c2.577 0 4.631-2.13 4.631-4.707v-5.13h-3.692v5.13zM4.631 29.538h5.215v-3.769H4.63c-.53 0-.938-.408-.938-.939v-5.215H0v5.215C0 27.408 2.054 29.538 4.63 29.538z" />
        </svg>
      </button>
    </div>
  );
}

CanvasZoomControls.propTypes = {
  canvasRef: PropTypes.object.isRequired,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};
